import { Button } from '@/app/common/components/button/Button'
import Label from '@/common/components/Label.tsx'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import Textarea from '@/common/components/Textarea.tsx'
import { SELECTABLE_REASONINGS } from '@/common/review/constants.ts'
import { applyMacroTokens, Macro } from '@/common/review/macros.ts'
import {
  Decision,
  ReviewDecisionFormData,
  ReviewDecisionFormSchema,
} from '@/common/review/schema.ts'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shad/ui/dialog.tsx'
import { RadioGroup, RadioGroupItem } from '@/shad/ui/radio-group.tsx'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shad/ui/select.tsx'
import { zodResolver } from '@hookform/resolvers/zod'
import { MutationStatus } from '@tanstack/react-query'
import { ReactNode, useEffect, useMemo, useRef } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Rendered inside a `DialogTrigger`; omit when the dialog is opened from outside. */
  trigger?: ReactNode
  title: string
  description?: ReactNode
  /** Wording of the two radio options, e.g. "the item submitted by the provider". */
  subject: string
  /** Decisions the reviewer is allowed to take on this record. */
  allowedDecisions?: Decision[]
  macros: Macro[]
  /** Values for the `{token}` placeholders inside the macro bodies. */
  macroTokens?: Record<string, string | undefined>
  defaultValues?: Partial<ReviewDecisionFormData>
  status: MutationStatus
  onSubmit: (values: ReviewDecisionFormData) => void
}

const ALL_DECISIONS: Decision[] = ['accepted', 'rejected']

/**
 * Admin review form (decision + reasoning + internal note + macro driven public
 * reply). Every review flow in the portal asks for the same four answers, so
 * the entity being reviewed is passed in rather than baked into the component.
 */
export default function ReviewDecisionDialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  subject,
  allowedDecisions = ALL_DECISIONS,
  macros,
  macroTokens,
  defaultValues,
  status,
  onSubmit,
}: Props) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
    setValue,
  } = useForm<ReviewDecisionFormData>({
    resolver: zodResolver(ReviewDecisionFormSchema),
    defaultValues,
  })

  const decision = useWatch({ control, name: 'decision' })
  const publicReply = useWatch({ control, name: 'public_reply' })
  /** Last macro body written into the reply, so manual edits are never lost. */
  const appliedMacroBody = useRef(defaultValues?.public_reply ?? '')

  // Compared by value so callers can pass an inline `macroTokens` literal
  // without retriggering the macro resolution on every render.
  const macroTokensKey = JSON.stringify(macroTokens ?? {})
  const resolvedMacros = useMemo(
    () =>
      macros.map((macro) => ({
        ...macro,
        body: applyMacroTokens(macro.body, macroTokens),
      })),
    [macros, macroTokensKey]
  )

  const applyMacro = (macro: Macro) => {
    appliedMacroBody.current = macro.body
    setValue('public_reply', macro.body)
  }

  // Selecting a decision pre-fills its macro, unless the reviewer already typed
  // a reply of their own.
  useEffect(() => {
    const macro = resolvedMacros.find((item) => item.code === decision)
    const replyIsUntouched =
      !publicReply || publicReply === appliedMacroBody.current
    if (macro && replyIsUntouched) applyMacro(macro)
    // `publicReply` is read but must not retrigger the effect on every keystroke.
  }, [decision, resolvedMacros])

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset(defaultValues)
      appliedMacroBody.current = defaultValues?.public_reply ?? ''
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-md font-semibold text-gray-900">
            {title}
          </DialogTitle>
          {description && (
            <div className="text-sm text-gray-600">{description}</div>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Decision</Label>
            <Controller
              name="decision"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  onValueChange={field.onChange}
                  value={field.value}
                  className="space-y-2"
                >
                  {allowedDecisions.includes('accepted') && (
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="accepted" id="accept" />
                      <Label weight="normal" htmlFor="accept">
                        I <span className="text-green-700">accept</span>{' '}
                        {subject}
                      </Label>
                    </div>
                  )}
                  {allowedDecisions.includes('rejected') && (
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="rejected" id="reject" />
                      <Label weight="normal" htmlFor="reject">
                        I <span className="text-red-700">reject</span> {subject}
                      </Label>
                    </div>
                  )}
                </RadioGroup>
              )}
            />
            {errors.decision && (
              <p className="text-sm text-red-500">{errors.decision.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label>Reasoning</Label>
            <Controller
              name="reasoning"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select reasoning" />
                  </SelectTrigger>
                  <SelectContent>
                    {SELECTABLE_REASONINGS.map((reasoning) => (
                      <SelectItem key={reasoning} value={reasoning}>
                        {reasoning}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.reasoning && (
              <p className="text-sm text-red-500">{errors.reasoning.message}</p>
            )}
          </div>

          <div className="space-y-1">
            <Label isRequired>Internal Note</Label>
            <Textarea
              {...register('justification')}
              placeholder="Enter your internal note"
              rows={2}
            />
            {errors.justification && (
              <p className="text-sm text-red-500">
                {errors.justification.message}
              </p>
            )}
          </div>

          <div className="space-y-1">
            <Label>Macros</Label>
            <Select
              onValueChange={(value) => {
                const macro = resolvedMacros.find(
                  (item) => item.title === value
                )
                if (macro) applyMacro(macro)
              }}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select macro" />
              </SelectTrigger>
              <SelectContent>
                {resolvedMacros.map((macro) => (
                  <SelectItem key={macro.title} value={macro.title}>
                    {macro.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label>Public Reply</Label>
            <Textarea
              {...register('public_reply')}
              placeholder="Enter your reply"
              rows={6}
            />
            {errors.public_reply && (
              <p className="text-sm text-red-500">
                {errors.public_reply.message}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <SubmitButton status={status} className="w-auto" />
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
